import type { LOCALE } from "@kivunova/kivufrontendcommon";
import { iremboLanguageFromUi } from "./iremboMembershipEnv";

/** Same signature as {@link useKivunovaTranslation} `t`. */
export type IremboTranslateFn = (key: string, defaultValue: string) => string;

let loadPromise: Promise<void> | null = null;

const ERR_WIDGET_TIMEOUT = "IremboPay global did not become available in time.";
const ERR_SCRIPT_URL_MISSING =
  "REACT_APP_IREMBOPAY_WIDGET_SCRIPT_URL is not set. Add it to your environment (see .env.example).";
const ERR_BROWSER_REQUIRED = "IremboPay widget requires a browser";

function waitForIremboPay(maxMs: number): Promise<void> {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const tick = () => {
      if (window.IremboPay?.initiate) {
        resolve();
        return;
      }
      if (Date.now() - start > maxMs) {
        reject(new Error(ERR_WIDGET_TIMEOUT));
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

export function getIremboPayWidgetScriptUrl(): string {
  const url = process.env.REACT_APP_IREMBOPAY_WIDGET_SCRIPT_URL?.trim();
  if (!url) {
    throw new Error(ERR_SCRIPT_URL_MISSING);
  }
  return url;
}

/**
 * Loads the IremboPay inline widget script once (idempotent).
 */
export function loadIremboPayScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error(ERR_BROWSER_REQUIRED));
  }
  if (window.IremboPay?.initiate) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }
  const src = getIremboPayWidgetScriptUrl();
  if (!document.querySelector(`script[data-irembopay-widget="true"]`)) {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.irembopayWidget = "true";
    script.onerror = () => {
      loadPromise = null;
    };
    document.head.appendChild(script);
  }
  loadPromise = waitForIremboPay(15_000).catch((e) => {
    loadPromise = null;
    throw e;
  });
  return loadPromise;
}

function pickWidgetLocale(
  Irembo: NonNullable<Window["IremboPay"]>,
  uiLang: LOCALE
): string {
  const lang = iremboLanguageFromUi(uiLang);
  const loc = Irembo.locale;
  if (!loc) {
    return lang;
  }
  if (lang === "FR" && loc.FR != null) return loc.FR;
  if (lang === "RW" && loc.RW != null) return loc.RW;
  return loc.EN ?? lang;
}

function translateLoadError(err: unknown, t: IremboTranslateFn): string {
  if (err instanceof Error) {
    if (err.message === ERR_WIDGET_TIMEOUT) {
      return t(
        "irembo-widget-global-timeout",
        "IremboPay global did not become available in time."
      );
    }
    if (err.message === ERR_SCRIPT_URL_MISSING) {
      return t(
        "irembo-config-script-url-missing",
        "REACT_APP_IREMBOPAY_WIDGET_SCRIPT_URL is not set. Add it to your environment (see .env.example)."
      );
    }
    if (err.message === ERR_BROWSER_REQUIRED) {
      return t(
        "irembo-widget-browser-required",
        "IremboPay widget requires a browser"
      );
    }
  }
  return t(
    "irembo-widget-load-unknown",
    "Could not load the payment widget. Please try again."
  );
}

function isIremboAlreadyPaidErrorCode(code: string): boolean {
  return code === "BAD_INVOICES_PAID";
}

/** True when the widget reports the invoice is already paid (MoMo completed). */
export function isIremboWidgetAlreadyPaidError(err: unknown): boolean {
  if (err == null) {
    return false;
  }
  if (typeof err === "object") {
    const o = err as Record<string, unknown>;
    const errors = o.errors;
    if (Array.isArray(errors)) {
      return errors.some((entry) => {
        if (typeof entry !== "object" || entry == null) {
          return false;
        }
        const code = (entry as Record<string, unknown>).code;
        return typeof code === "string" && isIremboAlreadyPaidErrorCode(code);
      });
    }
    if (typeof o.code === "string" && isIremboAlreadyPaidErrorCode(o.code)) {
      return true;
    }
  }
  return false;
}

function mapIremboErrorCodeToMessage(
  code: string,
  detail: string,
  t: IremboTranslateFn
): string {
  const fallback = () =>
    t("irembo-payment-failed", "Payment could not be completed.");

  switch (code) {
    case "INVALID_KEY":
      return t(
        "irembo-error-invalid-key",
        "Payment configuration error: invalid public key."
      );
    case "INVOICE_NOT_FOUND":
      return t(
        "irembo-error-invoice-not-found",
        "Invoice was not found. Refresh and try again."
      );
    case "BAD_INVOICES_PAID":
      return t(
        "irembo-error-invoice-already-paid",
        "This invoice has already been paid."
      );
    case "BAD_INVOICES_PAYMENT_EXPIRED":
      return t(
        "irembo-error-invoice-expired",
        "This invoice has expired. Start checkout again."
      );
    case "BAD_INVOICES_IN_BATCH":
      return t(
        "irembo-error-batch-invoice",
        "This invoice cannot be paid (batch invoice)."
      );
    case "INVOICE_MISSING":
      return t(
        "irembo-error-invoice-reference-missing",
        "Invoice reference is missing."
      );
    case "BAD_LOCALE":
      return t(
        "irembo-error-bad-locale",
        "Unsupported language for payment. Change your language and retry."
      );
    default:
      return detail || fallback();
  }
}

/** Maps IremboPay widget / API style errors to a short user-facing message. */
export function formatIremboWidgetCallbackError(
  err: unknown,
  t: IremboTranslateFn
): string {
  const fallback = () =>
    t("irembo-payment-failed", "Payment could not be completed.");

  if (err == null) {
    return fallback();
  }
  if (typeof err === "string") {
    return err.trim() || fallback();
  }
  if (err instanceof Error) {
    return err.message || fallback();
  }
  if (typeof err === "object") {
    const o = err as Record<string, unknown>;
    const message = typeof o.message === "string" ? o.message : undefined;

    const errors = o.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      const first = errors[0] as Record<string, unknown>;
      const errCode = typeof first.code === "string" ? first.code : "";
      const detail =
        typeof first.detail === "string" ? first.detail : errCode || fallback();
      return mapIremboErrorCodeToMessage(errCode, detail, t);
    }

    if (typeof o.code === "string") {
      return mapIremboErrorCodeToMessage(o.code, message ?? o.code, t);
    }
    if (message) {
      return message;
    }
  }
  return fallback();
}

export type IremboPayInitiateOptions = {
  /** For localized widget load and payment errors. */
  t: IremboTranslateFn;
  publicKey: string;
  invoiceNumber: string;
  /** App locale (from i18n), mapped to Irembo EN/FR/RW in {@link pickWidgetLocale}. */
  uiLanguage: LOCALE;
  onSuccess: () => void;
  onError: (message: string, err?: unknown) => void;
};

/**
 * Opens the IremboPay payment modal for an existing invoice (per IremboPay.initiate docs).
 */
export async function initiateIremboPayModal(
  opts: IremboPayInitiateOptions
): Promise<void> {
  const { t } = opts;
  try {
    await loadIremboPayScript();
  } catch (err: unknown) {
    opts.onError(translateLoadError(err, t));
    return;
  }
  const Irembo = window.IremboPay;
  if (!Irembo?.initiate) {
    opts.onError(
      t(
        "irembo-widget-not-loaded",
        "IremboPay widget did not load. Check the script URL and network."
      )
    );
    return;
  }
  Irembo.initiate({
    publicKey: opts.publicKey,
    invoiceNumber: opts.invoiceNumber,
    locale: pickWidgetLocale(Irembo, opts.uiLanguage),
    callback: (err: unknown) => {
      if (err == null || isIremboWidgetAlreadyPaidError(err)) {
        try {
          Irembo.closeModal?.();
        } catch {
          /* ignore */
        }
        opts.onSuccess();
        return;
      }
      opts.onError(formatIremboWidgetCallbackError(err, t), err);
    }
  });
}
